import { Grid, Skeleton } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroHierarchyFolderTree,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroStyledBox,
	CerebroText,
	CerebroInput,
} from '../../shared/components';
import { useApi } from '../../shared/context';
import { useDataRoomFolderHierarchyQuery } from '../../shared/hooks';
import { FormikUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';
import { DataRoomDocument } from '../../shared/models';

interface CopyDocumentToDrawerProps {
	dataRoomGuid: string | undefined;
	document: DataRoomDocument | null;
	close: () => void;
}

export const CopyDocumentToDrawer: React.FC<CopyDocumentToDrawerProps> = ({ dataRoomGuid, document, close }) => {
	const api = useApi();
	const client = useQueryClient();
	const snackbar = useSnackbar();
	const foldersQuery = useDataRoomFolderHierarchyQuery(dataRoomGuid);

	const validateForm = (values: CopyDocumentToFormValues): CopyDocumentToFormValueErrors => {
		const errors: CopyDocumentToFormValueErrors = {};

		if (!values.fileName) {
			errors.fileName = 'File name is required';
		}

		if (!values.folderGuid) {
			errors.folderGuid = 'Data room folder is required';
		} else {
			if (values.folderGuid == document?.folderGuid) {
				errors.folderGuid = 'Must select a different folder.';
			}
		}

		return errors;
	};

	const moveDocumentMutation = useMutation(
		async ({ values }: CopyDocumentToFormMutationVariables) => {
			const folderPatch = {
				folderGuid: values.folderGuid,
				fileName: values.fileName,
			};

			const { data, error } = await getApiResponseData<void>(
				api.post(CerebroApiEndpoints.Documents.CopyTo(dataRoomGuid, document?.guid), folderPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Data room document was copied successfully.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={document != null} onClose={() => close()} PaperProps={{ sx: { width: '700px' } }}>
			<CerebroHeading>{document?.name}</CerebroHeading>

			<CerebroText sx={{ marginTop: 2, marginBottom: 2 }}>
				This action will create a copy of the document. You can edit the name of the document if needed, and
				select the folder for the copy.
			</CerebroText>

			<Formik
				initialValues={
					{
						folderGuid: document?.folderGuid,
						fileName: document?.name ?? '',
					} as CopyDocumentToFormValues
				}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(moveDocumentMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={12}>
									<CerebroLabel id="fileName">File Name</CerebroLabel>
									<Field
										id="fileName"
										as={CerebroInput}
										name="fileName"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('fileName', errors, touched)}
									/>
									{FormikUtils.IsInvalid('fileName', errors, touched) && (
										<CerebroText variant="error">{errors.fileName}</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel>Data Room Folder</CerebroLabel>
									{foldersQuery.isLoading && <Skeleton height={60} />}
									{foldersQuery.isSuccess && (
										<CerebroStyledBox alignitems="left" sx={{ marginTop: 1 }}>
											<CerebroHierarchyFolderTree
												folder={foldersQuery.data}
												selectedGuid={values.folderGuid}
												hidePrivateFolders={false}
												onSelected={value => {
													values.folderGuid = value;
													setFieldValue('folderGuid', value);
												}}
											/>
										</CerebroStyledBox>
									)}
									{FormikUtils.IsInvalid('folderGuid', errors, touched) && (
										<CerebroText variant="error">{errors.folderGuid}</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type CopyDocumentToFormMutationVariables = FormikUtils.MutateOnSubmitVariables<CopyDocumentToFormValues>;
interface CopyDocumentToFormValues {
	folderGuid: string | undefined;
	fileName: string;
}
type CopyDocumentToFormValueErrors = FormikErrors<CopyDocumentToFormValues>;
